/**
 * Per-route SEO copy for public marketing pages.
 *
 * Brand Desk defaults (2026-09-21) adjusted to the live App.js inventory.
 * Canonical host is www. Do not invent UEI / CAGE / certs / client names.
 */

import { OG_PREVIEW } from './staticAssets';
import { UPDATE_SEO } from './snowflakeUpdates';

export const SITE_ORIGIN = 'https://www.myorglab.com';
export const SITE_NAME = 'MyOrgLab';

/**
 * Indexable public routes only. Hash anchors, auth shells, and redirects
 * are omitted so sitemap.xml and <link rel="canonical"> stay aligned.
 */
export const PUBLIC_SEO = {
  '/': {
    title: 'MyOrgLab | AI analytics built in your cloud account',
    description:
      'Governed conversational analytics and decision support built in your Snowflake and cloud accounts, not a multi-tenant SaaS bolt-on.',
  },
  '/company': {
    title: 'About MyOrgLab | AI analytics built in your cloud account',
    description:
      'MyOrgLab builds AI and analytics products in your cloud account, grounded in governed data and shared business meaning.',
  },
  '/contact': {
    title: 'Contact | MyOrgLab',
    description:
      'Talk with MyOrgLab about governed AI analytics and data platforms built in your cloud account.',
  },
  '/semantic-hr': {
    title: 'Semantic HR | Snowflake-native workforce intelligence | MyOrgLab',
    description:
      'Snowflake-native workforce intelligence for Workday and SuccessFactors: governed metrics, Cortex agents, and embedded BI in your account.',
  },
  '/interactive-analytics': {
    title: 'Interactive analytics | MyOrgLab',
    description:
      'Trusted Power BI products and conversational analytics integrated into how work gets done, on governed data you own.',
  },
  '/platform-engineering': {
    title: 'Platform engineering | MyOrgLab',
    description:
      'Modern cloud data foundations and semantic platform engineering built for reuse, scale, and AI readiness in your accounts.',
  },
  '/pricing': {
    title: 'Pricing | MyOrgLab',
    description:
      'Engagement options for MyOrgLab AI analytics and data platform work built in your cloud account.',
  },
  '/delivery': {
    title: 'Agile delivery on Azure DevOps | MyOrgLab',
    description:
      'We deliver AI and decision support in your cloud account: early, frequent, tested increments built with your team, on agile, CI/CD, dbt, Airflow and Terraform.',
  },
  '/privacy-policy': {
    title: 'Privacy policy | MyOrgLab',
    description: 'Privacy policy for MyOrgLab (Monroe Acquisitions, LLC DBA).',
  },
  ...UPDATE_SEO,
};

/** Legacy paths that 3xx to a canonical page — keep out of the sitemap. */
export const LEGACY_REDIRECT_PATHS = [
  '/powerbi-development',
  '/data-engineering',
  '/sports-intelligence',
];

/** Old sitemap URLs with no live public route. */
export const RETIRED_SITEMAP_PATHS = [
  '/custom-application',
  '/recruiting',
];

export const SITEMAP_PATHS = Object.keys(PUBLIC_SEO);

export function canonicalUrl(path) {
  if (path === '/') return `${SITE_ORIGIN}/`;
  return `${SITE_ORIGIN}${path}`;
}

export function getSeoForPath(path) {
  const seo = PUBLIC_SEO[path];
  if (!seo) {
    throw new Error(`Missing SEO copy for public route: ${path}`);
  }
  return {
    path,
    title: seo.title,
    description: seo.description,
    canonical: canonicalUrl(path),
    ogImage: OG_PREVIEW,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    siteName: SITE_NAME,
  };
}

/** Client-rendered unknown routes. Kept out of PUBLIC_SEO and the sitemap. */
export const NOT_FOUND_SEO = {
  title: 'Page not found | MyOrgLab',
  description: 'The page you requested is not on the MyOrgLab website.',
  robots: 'noindex,follow',
};

/**
 * Canonical for an unknown path, or null when it would point at the homepage
 * or leave this origin. Query and hash are dropped.
 */
export function notFoundCanonical(pathname) {
  if (typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return null;
  if (pathname.includes('..') || pathname.includes('\\') || pathname.includes('://')) return null;
  const pathOnly = pathname.split(/[?#]/)[0];
  if (!pathOnly || pathOnly === '/') return null;
  return `${SITE_ORIGIN}${encodeURI(pathOnly)}`;
}

function shellNodes(selector) {
  return [...document.head.querySelectorAll(selector)].filter(
    (node) => !node.hasAttribute('data-react-helmet'),
  );
}

function upsertShellTag({ selector, create, attr, value }) {
  const restores = [];
  const nodes = shellNodes(selector);

  if (value == null) {
    nodes.forEach((node) => {
      const parent = node.parentNode;
      const next = node.nextSibling;
      node.remove();
      restores.push(() => {
        if (!parent) return;
        if (next && next.parentNode === parent) parent.insertBefore(node, next);
        else parent.appendChild(node);
      });
    });
    return restores;
  }

  if (nodes.length === 0) {
    if (!create) return restores;
    const node = create();
    node.setAttribute(attr, value);
    document.head.appendChild(node);
    restores.push(() => node.remove());
    return restores;
  }

  nodes.forEach((node) => {
    const prev = node.getAttribute(attr);
    node.setAttribute(attr, value);
    restores.push(() => {
      if (prev == null) node.removeAttribute(attr);
      else node.setAttribute(attr, prev);
    });
  });
  return restores;
}

function metaName(name) {
  return () => {
    const el = document.createElement('meta');
    el.setAttribute('name', name);
    return el;
  };
}

function metaProperty(property) {
  return () => {
    const el = document.createElement('meta');
    el.setAttribute('property', property);
    return el;
  };
}

/**
 * The SPA shell in index.html bakes homepage title, description, canonical, and
 * Open Graph into every response. react-helmet only rewrites tags it owns
 * (`data-react-helmet`), so a client route would keep the homepage canonical
 * unless those shell tags are updated in place.
 *
 * Returns a restore function for client navigations away from the page.
 */
export function applyPublicShellHead(path) {
  const seo = getSeoForPath(path);
  const prevTitle = document.title;
  document.title = seo.title;

  const restores = [
    () => {
      document.title = prevTitle;
    },
    ...upsertShellTag({
      selector: 'meta[name="description"]',
      create: metaName('description'),
      attr: 'content',
      value: seo.description,
    }),
    ...upsertShellTag({
      selector: 'link[rel="canonical"]',
      create: () => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        return el;
      },
      attr: 'href',
      value: seo.canonical,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:title"]',
      create: metaProperty('og:title'),
      attr: 'content',
      value: seo.title,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:description"]',
      create: metaProperty('og:description'),
      attr: 'content',
      value: seo.description,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:url"]',
      create: metaProperty('og:url'),
      attr: 'content',
      value: seo.canonical,
    }),
    ...upsertShellTag({
      selector: 'meta[name="twitter:title"]',
      create: metaName('twitter:title'),
      attr: 'content',
      value: seo.title,
    }),
    ...upsertShellTag({
      selector: 'meta[name="twitter:description"]',
      create: metaName('twitter:description'),
      attr: 'content',
      value: seo.description,
    }),
  ];

  return () => {
    restores.reverse().forEach((restore) => restore());
  };
}

/**
 * Returns a restore function for client navigations away from the 404.
 */
export function applyNotFoundShellHead(pathname) {
  const canonical = notFoundCanonical(pathname);
  const prevTitle = document.title;
  document.title = NOT_FOUND_SEO.title;

  const restores = [
    () => {
      document.title = prevTitle;
    },
    ...upsertShellTag({
      selector: 'meta[name="description"]',
      create: metaName('description'),
      attr: 'content',
      value: NOT_FOUND_SEO.description,
    }),
    ...upsertShellTag({
      selector: 'meta[name="robots"]',
      create: metaName('robots'),
      attr: 'content',
      value: NOT_FOUND_SEO.robots,
    }),
    ...upsertShellTag({
      selector: 'link[rel="canonical"]',
      create: canonical
        ? () => {
            const el = document.createElement('link');
            el.setAttribute('rel', 'canonical');
            return el;
          }
        : null,
      attr: 'href',
      value: canonical,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:title"]',
      create: metaProperty('og:title'),
      attr: 'content',
      value: NOT_FOUND_SEO.title,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:description"]',
      create: metaProperty('og:description'),
      attr: 'content',
      value: NOT_FOUND_SEO.description,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:url"]',
      create: canonical ? metaProperty('og:url') : null,
      attr: 'content',
      value: canonical,
    }),
    ...upsertShellTag({
      selector: 'meta[property="og:type"]',
      create: metaProperty('og:type'),
      attr: 'content',
      value: 'website',
    }),
    ...upsertShellTag({
      selector: 'meta[name="twitter:title"]',
      create: metaName('twitter:title'),
      attr: 'content',
      value: NOT_FOUND_SEO.title,
    }),
    ...upsertShellTag({
      selector: 'meta[name="twitter:description"]',
      create: metaName('twitter:description'),
      attr: 'content',
      value: NOT_FOUND_SEO.description,
    }),
  ];

  return () => {
    restores.reverse().forEach((restore) => restore());
  };
}
